import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ProjectsApi,
  ProjectListQuery,
  OwnerContext,
} from "@/services/api/projects";
import { CreateProjectModal } from "@/components/project/CreateProjectModal";
import { useUserFlowIntegration } from "@/hooks/useUserFlowIntegration";
import { Project, ProjectStatus } from "@/types";
import { logger } from "@/utils";
import {
  Plus,
  Search,
  Folder,
  Lock,
  Globe,
  Clock,
  Trash2,
  ArrowRightLeft,
  Settings,
} from "lucide-react";
import { ProjectTransferDialog } from "@/components/project/ProjectTransferDialog";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { canManageOrganization } from "@/utils/organizationPermissions";

type ProjectStatusFilter =
  | "all"
  | ProjectStatus.ACTIVE
  | ProjectStatus.ARCHIVED;
type ProjectSort = "createdAt" | "updatedAt" | "name";
const PROJECT_SKELETON_KEYS = [
  "project-skeleton-1",
  "project-skeleton-2",
  "project-skeleton-3",
  "project-skeleton-4",
  "project-skeleton-5",
  "project-skeleton-6",
];

const isProjectStatusFilter = (value: string): value is ProjectStatusFilter =>
  value === "all" ||
  value === ProjectStatus.ACTIVE ||
  value === ProjectStatus.ARCHIVED;

const isProjectSort = (value: string): value is ProjectSort =>
  value === "createdAt" || value === "updatedAt" || value === "name";

export const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { openProject, createAndOpenProject } = useUserFlowIntegration();
  const location = useLocation();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const { currentContext, organizations } = useOrganizationStore();
  const activeOrgSlug =
    orgSlug ??
    (currentContext?.type === "organization"
      ? currentContext.orgSlug
      : undefined);
  const activeOrganization = activeOrgSlug
    ? organizations.find((organization) => organization.slug === activeOrgSlug)
    : undefined;
  const canManageActiveOrganization = canManageOrganization(
    activeOrganization?.membership?.role,
  );
  const ownerContext = useMemo<OwnerContext | undefined>(
    () =>
      activeOrgSlug
        ? { type: "organization", orgSlug: activeOrgSlug }
        : undefined,
    [activeOrgSlug],
  );

  // State
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [selectedProjectForTransfer, setSelectedProjectForTransfer] =
    useState<Project | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProjectStatusFilter>("all");
  const [sortBy, setSortBy] = useState<ProjectSort>("updatedAt");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const query: ProjectListQuery = {
        page,
        limit: 12,
        sortBy,
        sortOrder: "desc",
      };

      if (searchQuery.trim()) {
        query.search = searchQuery.trim();
      }

      if (statusFilter !== "all") {
        query.status = statusFilter;
      }

      const response = await ProjectsApi.getProjects(query, ownerContext);
      if (response.success && response.data) {
        setProjects(response.data.projects);
        setTotalPages(response.data.totalPages);
      } else {
        setError("Failed to load projects");
      }
    } catch (err) {
      logger.error("Error loading projects:", err);
      setError("Failed to load projects");
    } finally {
      setIsLoading(false);
    }
  }, [ownerContext, page, searchQuery, sortBy, statusFilter]);

  // Load projects on mount, when filters/context change, and when returning to this page.
  useEffect(() => {
    loadProjects();
  }, [loadProjects, location.pathname]);

  const handleCreateProject = async (data: {
    name: string;
    description: string;
    isPrivate: boolean;
  }) => {
    const projectId = await createAndOpenProject(data, ownerContext);
    if (projectId) {
      logger.info("Project created and opened successfully", { projectId });
      setShowCreateDialog(false);
      loadProjects();
    } else {
      throw new Error("Failed to create project");
    }
  };

  const handleOpenProject = (projectId: string) => {
    openProject(projectId, ownerContext);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    const projectId = projectToDelete.id;
    setProjectToDelete(null);

    try {
      const response = await ProjectsApi.deleteProject(projectId);
      if (response.success) {
        await loadProjects();
      } else {
        setError("Failed to delete project");
      }
    } catch (err) {
      logger.error("Error deleting project:", err);
      setError("Failed to delete project");
    }
  };

  const handleTransferClick = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedProjectForTransfer(project);
    setShowTransferDialog(true);
  };

  const handleTransferDialogClose = () => {
    setShowTransferDialog(false);
    setSelectedProjectForTransfer(null);
    // Reload projects after transfer
    loadProjects();
  };

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const diffInHours = Math.floor(
      (now.getTime() - d.getTime()) / (1000 * 60 * 60),
    );

    if (diffInHours < 1) {
      return "Just now";
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) {
        return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
      } else {
        return d.toLocaleDateString();
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Projects</h1>
          <p className="text-muted-foreground">
            Manage your component library projects
          </p>
        </div>

        <div className="flex gap-2">
          {activeOrgSlug && canManageActiveOrganization && (
            <Button
              variant="outline"
              onClick={() => navigate(`/orgs/${activeOrgSlug}/settings`)}
              className="flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Org Settings
            </Button>
          )}

          <Button
            className="flex items-center gap-2"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="h-4 w-4" />
            New Project
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2">
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(event) => {
              if (isProjectStatusFilter(event.target.value)) {
                setStatusFilter(event.target.value);
              }
            }}
            className="flex h-10 w-[180px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>

          {/* Sort By */}
          <select
            value={sortBy}
            onChange={(event) => {
              if (isProjectSort(event.target.value)) {
                setSortBy(event.target.value);
              }
            }}
            className="flex h-10 w-[150px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <option value="updatedAt">Last Modified</option>
            <option value="createdAt">Date Created</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/15 text-destructive px-4 py-2 rounded">
          {error}
        </div>
      )}

      {/* Projects Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROJECT_SKELETON_KEYS.map((skeletonKey) => (
            <Card key={skeletonKey} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-1/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card
              key={project.id}
              className="cursor-pointer hover:shadow-lg transition-shadow group"
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1"
                    onClick={() => handleOpenProject(project.id)}
                  >
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Folder className="h-4 w-4" />
                      {project.name}
                      {project.visibility === "private" && (
                        <Lock className="h-3 w-3 text-muted-foreground" />
                      )}
                      {project.visibility === "public" && (
                        <Globe className="h-3 w-3 text-muted-foreground" />
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1 line-clamp-2">
                      {project.description || "No description"}
                    </CardDescription>
                  </div>

                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleTransferClick(project, e)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-primary"
                      title="Transfer to organization"
                    >
                      <ArrowRightLeft className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setProjectToDelete(project);
                      }}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      title="Delete project"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent
                className="pt-0"
                onClick={() => handleOpenProject(project.id)}
              >
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDate(project.updatedAt)}
                  </div>
                  <div className="flex items-center gap-1">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${
                        project.status === "active"
                          ? "bg-green-500"
                          : project.status === "archived"
                            ? "bg-yellow-500"
                            : "bg-gray-500"
                      }`}
                    />
                    {project.status}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Create Project Card */}
          <Card
            className="border-dashed border-2 border-muted-foreground/25 cursor-pointer hover:border-primary transition-colors"
            onClick={() => setShowCreateDialog(true)}
          >
            <CardContent className="flex items-center justify-center h-40">
              <div className="text-center">
                <Plus className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">
                  Create New Project
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-12">
          <Folder className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">No projects found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery
              ? "Try adjusting your search or filters"
              : "Get started by creating your first project"}
          </p>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Project
          </Button>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateProject}
      />

      {/* Project Transfer Dialog */}
      {selectedProjectForTransfer && (
        <ProjectTransferDialog
          open={showTransferDialog}
          onOpenChange={(open) => {
            if (!open) {
              handleTransferDialogClose();
            }
          }}
          projectId={selectedProjectForTransfer.id}
          projectName={selectedProjectForTransfer.name}
          componentCount={0} // TODO: Add component count to project data
        />
      )}

      <AlertDialog
        open={!!projectToDelete}
        onOpenChange={(open) => {
          if (!open) setProjectToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{projectToDelete?.name}
              &quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteProject}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
